import React from 'react';
import { useTranslation } from 'localization';
import { Tooltip } from 'primereact/tooltip';
import './ApproachTreeNodeAtom.scss';

export interface ApproachTreeNodeAtomProps {
  /** Unique node identifier */
  id: string;
  /** Display label */
  label: string;
  /** Icon class — PrimeIcon (e.g. "pi-box") or avatar class (e.g. "center-department-icon") */
  icon?: string;
  /** Nesting depth (0 = root) */
  level: number;
  /** Whether this node can expand/collapse */
  expandable?: boolean;
  /** Whether this node is currently expanded */
  expanded?: boolean;
  /** Whether this node is selected */
  selected?: boolean;
  /** Whether this node is loading children */
  loading?: boolean;
  /** Error message if loading failed */
  error?: string;
  /** Called when the expand/collapse chevron is clicked */
  onToggle?: (id: string) => void;
  /** Called when the node label/row is clicked */
  onSelect?: (id: string) => void;
  /** Additional CSS class */
  className?: string;
  /** Test ID */
  'data-testid'?: string;
}

const ApproachTreeNodeAtom: React.FC<ApproachTreeNodeAtomProps> = ({
  id,
  label,
  icon,
  level,
  expandable = false,
  expanded = false,
  selected = false,
  loading = false,
  error,
  onToggle,
  onSelect,
  className,
  'data-testid': testId,
}) => {
  const { t } = useTranslation();

  const classes = [
    'approach-tree-node',
    selected && 'approach-tree-node--selected',
    className,
  ].filter(Boolean).join(' ');

  const indent = { paddingLeft: `calc(${level} * var(--spacing-md))` };

  const handleChevronClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle?.(id);
  };

  const handleRowClick = () => {
    onSelect?.(id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleRowClick();
    } else if (expandable && (e.key === 'ArrowRight' ? !expanded : e.key === 'ArrowLeft' && expanded)) {
      e.preventDefault();
      onToggle?.(id);
    }
  };

  const errorTooltipId = `err-${id.replaceAll('.', '-')}`;

  return (
    <div
      className={classes}
      style={indent}
      data-testid={testId}
      data-level={level}
      role="treeitem"
      tabIndex={0}
      aria-expanded={expandable ? expanded : undefined}
      aria-selected={selected}
      onClick={handleRowClick}
      aria-busy={loading || undefined}
      onKeyDown={handleKeyDown}
    >
      {/* Pointer affordance only: the treeitem itself is the one focusable control, and
          ArrowRight / ArrowLeft expand and collapse it (WAI-ARIA tree pattern). A focusable
          toggle nested in the treeitem would be a second, unnamed tab stop per row. */}
      <span className="approach-tree-node__chevron" aria-hidden="true" onClick={handleChevronClick}>
        {(() => {
          if (loading) {
            return <i className="pi pi-spin pi-spinner" />;
          }
          if (expandable) {
            return <i className={`pi ${expanded ? 'pi-chevron-down' : 'pi-chevron-right'}`} />;
          }
          return null;
        })()}
      </span>

      {icon && (
        icon.startsWith('pi-')
          ? <i className={`approach-tree-node__icon pi ${icon}`} />
          : <i className={`approach-tree-node__icon approach-tree-node__icon--avatar ${icon}`} />
      )}

      <span className="approach-tree-node__label" title={label}>
        {label}
      </span>

      {error && (
        <>
          <i
            className={`approach-tree-node__error pi pi-exclamation-triangle ${errorTooltipId}`}
            data-pr-tooltip={error}
            aria-label={t('marketplace.approachExplorer.error')}
          />
          <Tooltip target={`.${errorTooltipId}`} position="right" />
        </>
      )}
    </div>
  );
};

ApproachTreeNodeAtom.displayName = 'ApproachTreeNodeAtom';

export default ApproachTreeNodeAtom;
